import { useTheme } from '@emotion/react';
import React, { useEffect, useRef, useState } from 'react';

/**
 * Custom Cursor Component
 * Optimized for silky-smooth 120 FPS hardware-accelerated tracking.
 * Zero style tag injections or rotation.
 */
function Cursor() {
  const cursorRef = useRef(null);
  const [isAttacked, setIsAttacked] = useState(false);
  const theme = useTheme();
  const primaryColor = theme?.palette?.primary?.main || 'rgb(255, 78, 35)';

  useEffect(() => {
    let mouseX = -100;
    let mouseY = -100;
    let currentX = -100;
    let currentY = -100;
    let animId = null;

    const onMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    const onTouchMove = (e) => {
      if (e.touches && e.touches[0]) {
        mouseX = e.touches[0].clientX;
        mouseY = e.touches[0].clientY;
      }
    };

    let vx = 0;
    let vy = 0;
    let wasRestrained = false;

    // Smooth physics loop for buttery cursor motion + symbiote web restraint & snap
    const loop = () => {
      let targetX = mouseX;
      let targetY = mouseY;
      const restraint = window.__venomCursorRestraint;

      if (restraint && restraint.active) {
        targetX = restraint.x;
        targetY = restraint.y;
        wasRestrained = true;
      } else if (wasRestrained) {
        // Just snapped free from the web! Slingshot immediately back to real mouse pointer
        wasRestrained = false;
        vx = (mouseX - currentX) * 0.55;
        vy = (mouseY - currentY) * 0.55;
      }

      // High-performance snappy spring-damper physics (zero delay return)
      const k = 0.36;
      const damping = 0.68;
      const ax = (targetX - currentX) * k;
      const ay = (targetY - currentY) * k;

      vx = (vx + ax) * damping;
      vy = (vy + ay) * damping;

      currentX += vx;
      currentY += vy;

      // Report current visual position back so web threads latch to the exact drawn cursor pixel
      if (restraint && restraint.active) {
        restraint.visualX = currentX;
        restraint.visualY = currentY;
      }

      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate3d(${currentX - 15}px, ${currentY - 15}px, 0)`;
      }

      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    // Listen for venom attack state (without causing rapid layout thrashing)
    let attackTimeout = null;
    const handleVenomAttack = (e) => {
      const active = Boolean(e.detail && e.detail.intensity > 0.1);
      setIsAttacked((prev) => {
        if (prev !== active) return active;
        return prev;
      });

      clearTimeout(attackTimeout);
      if (active) {
        attackTimeout = setTimeout(() => {
          setIsAttacked(false);
        }, 300);
      }
    };

    window.addEventListener('venom-attack', handleVenomAttack);

    return () => {
      cancelAnimationFrame(animId);
      clearTimeout(attackTimeout);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('venom-attack', handleVenomAttack);
    };
  }, []);

  return (
    <div
      ref={cursorRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: 30,
        height: 30,
        borderRadius: '50%',
        border: isAttacked ? '3px solid #9ca3af' : `2px solid ${primaryColor}`,
        boxShadow: isAttacked
          ? '0 0 16px #9ca3af, 0 0 30px rgba(156, 163, 175, 0.5)'
          : `0 0 10px ${primaryColor}`,
        pointerEvents: 'none',
        zIndex: 99999,
        willChange: 'transform',
        transition: 'border 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        backgroundColor: isAttacked ? 'rgba(156, 163, 175, 0.18)' : 'transparent',
      }}
    />
  );
}

export default Cursor;
